import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, Vehicle, VehicleType, SailPowertrain } from './api';
import { listAudioInputDevices, Recorder } from './recorder/recorder';
import { encodeFlac } from './recorder/flac';
import * as queue from './recorder/uploadQueue';
import {
  WsClient, WsMessage, AnalysisResult, UnmixMethod,
  MicLocation, MIC_LOCATION_LABELS,
} from './lib/ws';
import { DiagnosticModal } from './DiagnosticModal';
import { TrendsModal } from './TrendsModal';
import {
  RmsChart, LiveRmsChart,
  LiveSpectrogramChart, SpectrogramSegment,
  LiveComponentsChart, ComponentsSegment,
} from './RmsChart';
import { LiveEnginePanel } from './LiveEnginePanel';
import { FeedbackPanel } from './FeedbackPanel';
import { CalibrateCylindersModal } from './CalibrateCylindersModal';
import { CalInstructionsModal } from './CalInstructionsModal';

const LIVE_MAX_SEGMENTS = 10;
const MAX_RECENT_CLIPS = 10;
const SELECTED_VEHICLE_KEY = 'engineAcoustics.selectedVehicleId';
const UNMIX_METHOD_KEY = 'engineAcoustics.unmixMethod';
const MIC_LOCATION_KEY = 'engineAcoustics.micLocation';
const PURPOSE_HISTORY_KEY = 'engineAcoustics.purposeHistory';
const PURPOSE_HISTORY_MAX = 10;
// Engine config (cylinders / strokes / valves) is now persisted per-vehicle
// on the server. The localStorage keys that used to hold these globally
// were removed when the data moved to the `vehicles` table — see the
// db.ts migration block.

const ONE_TO_24 = Array.from({ length: 24 }, (_, i) => i + 1);

// Reconnect schedule: 1, 2, 4, 8, 15, 30 seconds, then 30s thereafter.
const RECONNECT_BACKOFF_MS = [1000, 2000, 4000, 8000, 15000, 30000];

// If the server doesn't ack a binary upload within this window we assume the
// connection is wedged and force a reconnect; the queued clip stays put.
const UPLOAD_ACK_TIMEOUT_MS = 30000;

type LiveMode = 'rms' | 'spectrogram' | 'components';

interface Props {
  username: string;
  onLogout: () => void;
}

interface ClipLabels {
  gauge_rpm: number | null;
  powertrain: 'engine' | 'ev' | null;
  incident_keywords: string | null;
}

interface ClipEntry {
  recordingId?: number;
  startedAt: number;
  durationSec: number;
  sampleRate: number;
  sizeBytes?: number;
  status: 'encoding' | 'queued' | 'uploading' | 'analysing' | 'done' | 'error';
  error?: string;
  result?: AnalysisResult;
  labels?: ClipLabels;
}

interface LiveSegmentData {
  rms: number[];
  spec: SpectrogramSegment;
  comp: ComponentsSegment;
  method: UnmixMethod;
  receivedAt: number;
  /** Consensus RPM after harmonic correction. */
  rpm?: number;
  /** Consensus RPM BEFORE correction (transparency / diagnostic). */
  rpmRaw?: number;
  /** Diagnostic when RPM detection failed for this segment. */
  rpmError?: string;
  /** Per-detector results — each has lock_factor + corrected_rpm. */
  rpmMethods?: import('./lib/ws').RpmMethodResult[];
  rpmAgreement?: import('./lib/ws').RpmAgreement;
  rpmCorrection?: import('./lib/ws').RpmCorrectionKind;
  /** Human-readable summary of harmonic-locking patterns; "no
   *  harmonic locking — clean signal" when healthy. */
  rpmDiagnostic?: string;
}

// Promise-based one-shot waker the uploader uses to sleep until the recorder
// enqueues something or the WS reconnects. Resolving the same waker twice is
// harmless because the loop allocates a new one each cycle.
interface Waker { promise: Promise<void>; resolve: () => void }
function makeWaker(): Waker {
  let resolve: () => void = () => {};
  const promise = new Promise<void>((r) => { resolve = r; });
  return { promise, resolve };
}

export function Dashboard({ username, onLogout }: Props) {
  // ---- Vehicles ----
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(() => {
    const v = localStorage.getItem(SELECTED_VEHICLE_KEY);
    const n = v ? Number(v) : NaN;
    return Number.isInteger(n) && n > 0 ? n : null;
  });

  // ---- Capture / streaming ----
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string>('');
  const [clipLengthSec, setClipLengthSec] = useState(5);
  const [intervalSec, setIntervalSec] = useState(15);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState<string>('idle');
  const [error, setError] = useState<string | null>(null);
  /** Live AnalyserNode tee'd off the recorder's mic. Set when recording
   *  starts and the AudioContext exists, cleared on stop. The
   *  LiveEnginePanel reads from it to drive the level meter, the live
   *  RPM detector, and the dual-line trace. */
  const [liveAnalyser, setLiveAnalyser] = useState<AnalyserNode | null>(null);

  // ---- Modals ----
  const [showConfig, setShowConfig] = useState(false);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [showCalibrate, setShowCalibrate] = useState(false);
  const [showCalInstructions, setShowCalInstructions] = useState(false);
  /** When non-null, a Label modal is open for the clip whose startedAt
   *  matches this value. */
  const [labelTarget, setLabelTarget] = useState<number | null>(null);
  /** Same, for the Diagnostic modal. */
  const [diagnoseTarget, setDiagnoseTarget] = useState<number | null>(null);
  const [showTrends, setShowTrends] = useState(false);
  /** Cached baseline analysis for the currently-selected vehicle, used
   *  by the Diagnostic modal's drift section. */
  const [baselineAnalysis, setBaselineAnalysis] = useState<AnalysisResult | null>(null);

  // ---- Clips list (capped) and session counters ----
  const [clips, setClips] = useState<ClipEntry[]>([]);
  const [sessionTotal, setSessionTotal] = useState(0);
  /** Bumped each time an analysis completes — the FeedbackPanel watches
   *  this so it can re-fetch immediately without waiting for its
   *  poll tick. */
  const [feedbackRefreshTick, setFeedbackRefreshTick] = useState(0);
  const [sessionErrors, setSessionErrors] = useState(0);

  // ---- Connectivity / queue state (drives offline banner) ----
  const [wsConnected, setWsConnected] = useState(false);
  const [queueCount, setQueueCount] = useState(0);
  const [evictedTotal, setEvictedTotal] = useState(0);

  // ---- Live plot ----
  const [showLive, setShowLive] = useState(true);
  const [liveMode, setLiveMode] = useState<LiveMode>('rms');
  const [liveClips, setLiveClips] = useState<LiveSegmentData[]>([]);

  // ---- Unmix method (block or SSA) — persisted across reloads ----
  const [unmixMethod, setUnmixMethod] = useState<UnmixMethod>(() => {
    const v = localStorage.getItem(UNMIX_METHOD_KEY);
    return v === 'ssa' ? 'ssa' : 'block';
  });
  useEffect(() => {
    localStorage.setItem(UNMIX_METHOD_KEY, unmixMethod);
  }, [unmixMethod]);

  // ---- Mic location (persisted) ----
  const [micLocation, setMicLocation] = useState<MicLocation>(() => {
    const v = localStorage.getItem(MIC_LOCATION_KEY);
    return v === 'phone_hood' || v === 'external_hood' ? v : 'phone_cab';
  });
  useEffect(() => {
    localStorage.setItem(MIC_LOCATION_KEY, micLocation);
  }, [micLocation]);

  // Engine description is now sourced from the currently-selected vehicle
  // in the `vehicles` array (see selectedVehicle below). Editing it goes
  // through `updateVehicleEngine`, which PUTs to the server and updates
  // the local vehicles list.

  // ---- Trip metadata (km + purpose) and purpose history ----
  const [tripStartKm, setTripStartKm] = useState<string>('');
  const [tripPurpose, setTripPurpose] = useState<string>('');
  const [purposeHistory, setPurposeHistory] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(PURPOSE_HISTORY_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string').slice(0, PURPOSE_HISTORY_MAX) : [];
    } catch { return []; }
  });
  useEffect(() => {
    localStorage.setItem(PURPOSE_HISTORY_KEY, JSON.stringify(purposeHistory));
  }, [purposeHistory]);

  const liveRms = useMemo(() => liveClips.flatMap((c) => c.rms), [liveClips]);
  const liveClipBoundaries = useMemo(() => {
    const out: number[] = [];
    let acc = 0;
    for (const c of liveClips) {
      if (acc > 0) out.push(acc);
      acc += c.rms.length;
    }
    return out;
  }, [liveClips]);
  const liveSpectrogramSegments = useMemo(
    () => liveClips.map((c) => c.spec),
    [liveClips],
  );
  const liveComponentSegments = useMemo(
    () => liveClips.map((c) => c.comp),
    [liveClips],
  );
  const liveUpdatedAt = liveClips.length > 0
    ? liveClips[liveClips.length - 1].receivedAt
    : null;

  // Stats over the currently displayed (recent) clips.
  const visibleStats = useMemo(() => {
    let count = 0;
    let errors = 0;
    let blocks = 0;
    let sum = 0;
    let min = Infinity;
    let max = -Infinity;
    for (const c of clips) {
      count++;
      if (c.status === 'error') errors++;
      if (c.result) {
        const arr = c.result.rms.rms;
        blocks += arr.length;
        for (const v of arr) {
          sum += v;
          if (v < min) min = v;
          if (v > max) max = v;
        }
      }
    }
    return {
      count,
      errors,
      blocks,
      mean: blocks > 0 ? sum / blocks : 0,
      min: blocks > 0 ? min : 0,
      max: blocks > 0 ? max : 0,
    };
  }, [clips]);

  const recorderRef = useRef<Recorder | null>(null);
  const wsRef = useRef<WsClient | null>(null);
  const cancelRef = useRef<{ cancelled: boolean }>({ cancelled: false });

  // Uploader plumbing.
  const uploaderRunningRef = useRef(false);
  const uploaderWakerRef = useRef<Waker | null>(null);
  const pendingAckRef = useRef<
    { resolve: (id: number) => void; reject: (e: Error) => void } | null
  >(null);
  const handleWsMessageRef = useRef<((m: WsMessage) => void) | null>(null);
  // Live mirror of `running` so the uploader closure sees current state.
  const runningRef = useRef(false);
  useEffect(() => { runningRef.current = running; }, [running]);

  // Selected vehicle name (for header subtitle and status label).
  const selectedVehicle = useMemo(
    () => vehicles.find((v) => v.id === selectedVehicleId) ?? null,
    [vehicles, selectedVehicleId],
  );

  // Engine config sourced from the selected vehicle (or 4/4/4 if none).
  // These read from the vehicle row, so switching vehicles in the dropdown
  // immediately swaps the displayed engine spec.
  const numCylinders = selectedVehicle?.num_cylinders ?? 4;
  const numStrokes: 2 | 4 = selectedVehicle?.num_strokes === 2 ? 2 : 4;
  const numValvesPerCylinder = selectedVehicle?.num_valves_per_cylinder ?? 4;

  // Toggle the selected vehicle's baseline recording. If the given
  // recording is already the baseline, clear it; else set it.
  const saveAsBaseline = useCallback(async (recordingId: number) => {
    if (selectedVehicleId == null || !selectedVehicle) return;
    const isBaseline = selectedVehicle.baseline_recording_id === recordingId;
    const target = isBaseline ? null : recordingId;
    const before = selectedVehicle;
    const after: Vehicle = { ...before, baseline_recording_id: target };
    setVehicles((prev) => prev.map((v) => (v.id === selectedVehicleId ? after : v)));
    try {
      await api.put<{ baseline_recording_id: number | null }>(
        `/api/vehicles/${selectedVehicleId}/baseline`,
        { recording_id: target },
      );
    } catch (e) {
      setVehicles((prev) => prev.map((v) => (v.id === selectedVehicleId ? before : v)));
      setError(`could not update baseline: ${(e as Error).message}`);
    }
  }, [selectedVehicleId, selectedVehicle]);

  // Persist an edit to the selected vehicle's engine config. Optimistically
  // updates the in-memory vehicles list so the UI reacts immediately; rolls
  // back on PUT failure.
  const updateVehicleEngine = useCallback(async (
    patch: Partial<Pick<Vehicle,
      'num_cylinders' | 'num_strokes' | 'num_valves_per_cylinder' | 'firing_order'
      | 'powertrain_kind'
    >>,
  ) => {
    if (selectedVehicleId == null || !selectedVehicle) return;
    const before = selectedVehicle;
    const after: Vehicle = { ...before, ...patch };
    setVehicles((prev) => prev.map((v) => (v.id === selectedVehicleId ? after : v)));
    try {
      const updated = await api.put<Vehicle>(`/api/vehicles/${selectedVehicleId}`, {
        num_cylinders: after.num_cylinders,
        num_strokes: after.num_strokes,
        num_valves_per_cylinder: after.num_valves_per_cylinder,
        firing_order: after.firing_order,
        powertrain_kind: after.powertrain_kind,
      });
      setVehicles((prev) => prev.map((v) => (v.id === selectedVehicleId ? updated : v)));
    } catch (e) {
      // Roll back so the UI matches what's actually on the server.
      setVehicles((prev) => prev.map((v) => (v.id === selectedVehicleId ? before : v)));
      setError(`could not save engine config: ${(e as Error).message}`);
    }
  }, [selectedVehicleId, selectedVehicle]);

  // ---- Load vehicles + devices on mount ----
  useEffect(() => {
    api.get<{ vehicles: Vehicle[] }>('/api/vehicles')
      .then((r) => {
        setVehicles(r.vehicles);
        if (selectedVehicleId != null && !r.vehicles.some((v) => v.id === selectedVehicleId)) {
          setSelectedVehicleId(null);
        }
        if (selectedVehicleId == null && r.vehicles.length === 1) {
          setSelectedVehicleId(r.vehicles[0].id);
        }
      })
      .catch((e) => setError((e as Error).message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedVehicleId == null) {
      localStorage.removeItem(SELECTED_VEHICLE_KEY);
    } else {
      localStorage.setItem(SELECTED_VEHICLE_KEY, String(selectedVehicleId));
    }
  }, [selectedVehicleId]);

  // Pull the selected vehicle's baseline analysis (if any) so the
  // Diagnostic modal can compute drift.
  useEffect(() => {
    if (selectedVehicleId == null) { setBaselineAnalysis(null); return; }
    let cancelled = false;
    api.get<{ baseline_recording_id: number | null; analysis: AnalysisResult | null }>(
      `/api/vehicles/${selectedVehicleId}/baseline-analysis`,
    ).then((r) => {
      if (!cancelled) setBaselineAnalysis(r.analysis);
    }).catch(() => {
      if (!cancelled) setBaselineAnalysis(null);
    });
    return () => { cancelled = true; };
  }, [selectedVehicleId, selectedVehicle?.baseline_recording_id]);

  const refreshDevices = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      const list = await listAudioInputDevices();
      setDevices(list);
      if (!deviceId && list.length) setDeviceId(list[0].deviceId);
    } catch (e) {
      setError('microphone permission required: ' + (e as Error).message);
    }
  }, [deviceId]);

  useEffect(() => { refreshDevices(); }, [refreshDevices]);

  // ---- Mic-rerouting watchdog (Android Auto, headset plugs, etc.) ----
  // Live state of the actually-attached audio track and a sticky warning
  // if it ever drifts from the user's chosen deviceId.
  const [activeMic, setActiveMic] = useState<{
    deviceId: string | null; label: string;
  } | null>(null);
  const [micWarning, setMicWarning] = useState<string | null>(null);

  // OS-level device-list changes (e.g. Android Auto plugging in/out)
  // re-fire `devicechange`; refresh and verify the chosen device still
  // exists.
  useEffect(() => {
    const onChange = () => {
      void refreshDevices();
      const stream = recorderRef.current;
      const info = stream?.activeStreamInfo;
      if (info) setActiveMic(info);
    };
    navigator.mediaDevices?.addEventListener?.('devicechange', onChange);
    return () => {
      navigator.mediaDevices?.removeEventListener?.('devicechange', onChange);
    };
  }, [refreshDevices]);

  // Refresh queue count from IndexedDB.
  const syncQueueCount = useCallback(async () => {
    try { setQueueCount(await queue.count()); } catch { /* db unavailable */ }
  }, []);

  // ---- WebSocket message handling (analysis results / saved acks) ----
  const handleWsMessage = useCallback((msg: WsMessage) => {
    if (msg.type === 'recording_saved' && pendingAckRef.current) {
      // Resolve the in-flight upload's ack waiter so the uploader can
      // dequeue the corresponding IndexedDB entry. The clip-list status
      // update happens below via the same handler.
      const cb = pendingAckRef.current;
      pendingAckRef.current = null;
      cb.resolve(msg.recording_id);
    }
    setClips((prev) => {
      const next = [...prev];
      const findRecent = (id?: number): number => {
        if (id != null) return next.findIndex((c) => c.recordingId === id);
        for (let i = 0; i < next.length; i++) {
          if (next[i].status === 'uploading') return i;
        }
        return -1;
      };
      if (msg.type === 'recording_saved') {
        const idx = findRecent();
        if (idx >= 0) {
          next[idx] = {
            ...next[idx],
            recordingId: msg.recording_id,
            sizeBytes: msg.size_bytes,
            status: 'analysing',
          };
        }
      } else if (msg.type === 'analysis_result') {
        const idx = findRecent(msg.recording_id);
        if (idx >= 0) {
          next[idx] = { ...next[idx], status: 'done', result: msg.result };
        }
        setLiveClips((prevLive) => {
          const seg: LiveSegmentData = {
            rms: msg.result.rms.rms,
            spec: {
              data: msg.result.spectrogram.data,
              numBlocks: msg.result.spectrogram.num_blocks,
              numBins: msg.result.spectrogram.num_bins,
            },
            comp: { components: msg.result.components.components },
            method: msg.result.components.method,
            receivedAt: Date.now(),
            rpm: msg.result.components.rpm,
            rpmRaw: msg.result.components.rpm_raw,
            // Either error indicates RPM detection failed for this clip;
            // `error` is the ICA-specific failure (components empty), and
            // `rpm_error` is the method-agnostic detector failure.
            rpmError: msg.result.components.rpm_error
                   ?? msg.result.components.error,
            rpmMethods: msg.result.components.rpm_methods,
            rpmAgreement: msg.result.components.rpm_agreement,
            rpmCorrection: msg.result.components.rpm_correction,
            rpmDiagnostic: msg.result.components.rpm_diagnostic,
          };
          const merged = prevLive.concat(seg);
          return merged.length > LIVE_MAX_SEGMENTS
            ? merged.slice(merged.length - LIVE_MAX_SEGMENTS)
            : merged;
        });
        setSessionTotal((n) => n + 1);
        // The server may have created one or more feedback prompts off
        // this analysis; nudge the panel to refresh now rather than at
        // its next 30 s poll.
        setFeedbackRefreshTick((n) => n + 1);
      } else if (msg.type === 'analysis_error') {
        const idx = findRecent(msg.recording_id);
        if (idx >= 0) {
          next[idx] = { ...next[idx], status: 'error', error: msg.error };
        }
        setSessionErrors((n) => n + 1);
      } else if (msg.type === 'error') {
        // Server rejected the upload (e.g. invalid meta or unknown vehicle).
        // Bubble up to the in-flight ack waiter so the uploader can drop the
        // clip rather than retry forever.
        if (pendingAckRef.current) {
          const cb = pendingAckRef.current;
          pendingAckRef.current = null;
          cb.reject(new Error(msg.error));
        }
        setError(msg.error);
      }
      return next;
    });
  }, []);
  handleWsMessageRef.current = handleWsMessage;

  // ---- Connect / reconnect a WS, waiting for the server's "ready" frame ----
  const connectWs = useCallback(async (): Promise<WsClient> => {
    const ws = new WsClient();
    let readyResolve: () => void = () => {};
    const readyPromise = new Promise<void>((r) => { readyResolve = r; });
    ws.on((msg) => {
      if (msg.type === 'ready') readyResolve();
    });
    ws.on((m) => handleWsMessageRef.current?.(m));
    ws.onClose(({ code, intentional }) => {
      setWsConnected(false);
      // Auth failure — give up reconnecting.
      if (code === 4401) {
        setError('session expired — please sign in again');
        cancelRef.current.cancelled = true;
      }
      // Unblock the in-flight upload (if any) so the uploader retries.
      if (!intentional && pendingAckRef.current) {
        const cb = pendingAckRef.current;
        pendingAckRef.current = null;
        cb.reject(new Error('connection lost'));
      }
      if (wsRef.current === ws) wsRef.current = null;
      // Wake the uploader so it notices and starts reconnect backoff.
      uploaderWakerRef.current?.resolve();
    });
    await ws.connect();
    await Promise.race([
      readyPromise,
      new Promise<void>((_, rej) =>
        setTimeout(() => rej(new Error('ready timeout')), 10000)),
    ]);
    setWsConnected(true);
    return ws;
  }, []);

  // ---- Uploader loop: drains the IndexedDB queue while there is work ----
  const uploaderLoop = useCallback(async () => {
    if (uploaderRunningRef.current) return;
    uploaderRunningRef.current = true;
    let backoffIdx = 0;
    try {
      while (!cancelRef.current.cancelled) {
        const pending = await queue.peekOldest();
        if (!pending) {
          // Queue empty — if the recorder has stopped, exit; otherwise sleep
          // until the recorder enqueues something or up to 30s.
          if (!runningRef.current && !recorderActiveRef.current) break;
          uploaderWakerRef.current = makeWaker();
          await Promise.race([
            uploaderWakerRef.current.promise,
            new Promise<void>((r) => setTimeout(r, 30000)),
          ]);
          uploaderWakerRef.current = null;
          continue;
        }

        // Make sure we have a live WS before we send.
        if (!wsRef.current?.isOpen()) {
          try {
            wsRef.current = await connectWs();
            backoffIdx = 0;
          } catch (e) {
            setStatus(`offline — retry in ${RECONNECT_BACKOFF_MS[backoffIdx] / 1000}s`);
            const wait = RECONNECT_BACKOFF_MS[Math.min(backoffIdx, RECONNECT_BACKOFF_MS.length - 1)];
            backoffIdx++;
            await sleep(wait);
            continue;
          }
        }

        const ws = wsRef.current;
        if (!ws || !ws.isOpen()) continue;

        // Mark the in-memory entry as uploading (no-op if it has scrolled
        // off the visible list).
        setClips((prev) => prev.map((c) =>
          c.startedAt === pending.clientId ? { ...c, status: 'uploading' } : c,
        ));
        setStatus(`uploading clip…`);

        let recordingId: number;
        try {
          const ackPromise = new Promise<number>((resolve, reject) => {
            pendingAckRef.current = { resolve, reject };
          });
          ws.sendMeta({
            sample_rate: pending.sampleRate,
            channels: pending.channels,
            duration_sec: pending.durationSec,
            vehicle_id: pending.vehicleId,
            unmix_method: pending.unmixMethod,
            trip_start_km: pending.tripStartKm ?? 0,
            trip_purpose: pending.tripPurpose ?? '(unspecified)',
            mic_location: pending.micLocation ?? 'phone_cab',
            num_cylinders: pending.numCylinders ?? 4,
            num_strokes: pending.numStrokes ?? 4,
            num_valves_per_cylinder: pending.numValvesPerCylinder ?? 4,
            mic_device_label: pending.micDeviceLabel,
          });
          ws.sendBinary(pending.flac);
          recordingId = await Promise.race([
            ackPromise,
            new Promise<number>((_, rej) =>
              setTimeout(() => rej(new Error('upload ack timeout')), UPLOAD_ACK_TIMEOUT_MS)),
          ]);
        } catch (e) {
          // Ack lost (connection drop or timeout) → leave clip queued, force
          // a reconnect, and try again on the next loop turn.
          pendingAckRef.current = null;
          const msg = (e as Error).message;
          if (msg === 'connection lost' || msg === 'upload ack timeout') {
            try { wsRef.current?.close(); } catch { /* ignore */ }
            wsRef.current = null;
            await sleep(500);
            continue;
          }
          // Server-side rejection (invalid meta, unknown vehicle). Drop the
          // clip — re-uploading the same bytes won't help.
          if (pending.id != null) await queue.deleteById(pending.id);
          setClips((prev) => prev.map((c) =>
            c.startedAt === pending.clientId
              ? { ...c, status: 'error', error: msg }
              : c,
          ));
          setSessionErrors((n) => n + 1);
          await syncQueueCount();
          continue;
        }

        // Saved successfully — drop from IndexedDB.
        if (pending.id != null) await queue.deleteById(pending.id);
        await syncQueueCount();
        // The 'analysing' status was already set by handleWsMessage when the
        // recording_saved frame arrived. Nothing else to do here.
        void recordingId;
      }
    } finally {
      uploaderRunningRef.current = false;
      // If we cancelled mid-loop, close any leftover WS so we don't leak.
      if (cancelRef.current.cancelled && wsRef.current) {
        try { wsRef.current.close(); } catch { /* ignore */ }
        wsRef.current = null;
        setWsConnected(false);
      }
    }
  // running and recorderActiveRef are read at loop time; deps below are the
  // stable callbacks the loop closes over.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectWs, syncQueueCount]);

  // Tracks whether the recorder loop is still capturing — when it stops the
  // uploader can exit once the queue drains.
  const recorderActiveRef = useRef(false);

  // Auto-resume the uploader on mount if there are leftover clips from a
  // previous session (page reload or browser restart).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const n = await queue.count().catch(() => 0);
      setQueueCount(n);
      if (n > 0 && !cancelled) {
        cancelRef.current = { cancelled: false };
        void uploaderLoop();
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = useCallback(async () => {
    if (selectedVehicleId == null) {
      setError('select or add a vehicle before starting');
      return;
    }
    const km = Number(tripStartKm);
    if (!Number.isFinite(km) || km < 0) {
      setError('enter a valid trip start odometer reading (0 or higher)');
      return;
    }
    const purpose = tripPurpose.trim();
    if (!purpose) {
      setError('enter a trip purpose');
      return;
    }
    // Promote this purpose to the front of the history (deduped, capped).
    setPurposeHistory((prev) => {
      const without = prev.filter((p) => p !== purpose);
      return [purpose, ...without].slice(0, PURPOSE_HISTORY_MAX);
    });
    setError(null);
    setLiveClips([]);
    setClips([]);
    setSessionTotal(0);
    setSessionErrors(0);
    setEvictedTotal(0);
    cancelRef.current = { cancelled: false };
    try {
      const recorder = new Recorder();
      await recorder.init(deviceId || undefined);
      recorderRef.current = recorder;

      // Snapshot the actual mic that the OS attached to. On Android with
      // Android Auto active this can silently differ from what we
      // requested, even with `deviceId: { exact: ... }`.
      const info = recorder.activeStreamInfo;
      setActiveMic(info);
      setMicWarning(null);
      if (info && deviceId && info.deviceId && info.deviceId !== deviceId) {
        const expected = devices.find((d) => d.deviceId === deviceId)?.label ?? deviceId;
        setMicWarning(
          `OS attached "${info.label || '(unknown)'}" instead of "${expected}". ` +
          `If Android Auto is active it may have re-routed the mic.`,
        );
      }

      setRunning(true);
      setStatus('running');
      recorderActiveRef.current = true;
      setLiveAnalyser(recorder.monitorAnalyser);
      // Kick off both loops. The uploader manages its own WS lifecycle.
      void uploaderLoop();
      void recordLoop();
    } catch (e) {
      setError((e as Error).message);
      await stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, selectedVehicleId, tripStartKm, tripPurpose, uploaderLoop]);

  const recordLoop = useCallback(async () => {
    while (!cancelRef.current.cancelled) {
      const recorder = recorderRef.current;
      const vid = selectedVehicleId;
      if (!recorder || vid == null) break;
      const startedAt = Date.now();
      const entry: ClipEntry = {
        startedAt,
        durationSec: clipLengthSec,
        sampleRate: recorder.sampleRate,
        status: 'encoding',
      };
      setClips((p) => [entry, ...p].slice(0, MAX_RECENT_CLIPS));
      try {
        setStatus(`recording ${clipLengthSec}s clip…`);
        const clip = await recorder.recordClip(clipLengthSec);
        if (cancelRef.current.cancelled) break;
        setStatus('encoding FLAC…');
        const flac = await encodeFlac(clip.samples, {
          sampleRate: clip.sampleRate, channels: 1, bitsPerSample: 16,
        });
        if (cancelRef.current.cancelled) break;
        const enq = await queue.enqueue({
          clientId: startedAt,
          createdAt: Date.now(),
          vehicleId: vid,
          sampleRate: clip.sampleRate,
          channels: 1,
          durationSec: clip.durationSec,
          unmixMethod,
          flac,
          tripStartKm: Number(tripStartKm),
          tripPurpose: tripPurpose.trim(),
          micLocation,
          numCylinders,
          numStrokes,
          numValvesPerCylinder,
          micDeviceLabel: recorder.activeStreamInfo?.label || undefined,
        });
        if (enq.evicted > 0) setEvictedTotal((n) => n + enq.evicted);
        await syncQueueCount();
        setClips((p) => p.map((c) =>
          c.startedAt === startedAt
            ? { ...c, status: 'queued', sizeBytes: flac.length }
            : c,
        ));
        // Wake the uploader if it's sleeping; restart it if it had exited.
        uploaderWakerRef.current?.resolve();
        if (!uploaderRunningRef.current) void uploaderLoop();
      } catch (e) {
        setClips((p) => p.map((c) =>
          c.startedAt === startedAt ? { ...c, status: 'error', error: (e as Error).message } : c,
        ));
        setSessionErrors((n) => n + 1);
      }
      if (cancelRef.current.cancelled) break;
      const elapsed = (Date.now() - startedAt) / 1000;
      const wait = Math.max(0, intervalSec - elapsed);
      setStatus(`waiting ${wait.toFixed(1)}s until next clip…`);
      await sleep(wait * 1000);
    }
    recorderActiveRef.current = false;
    // Wake uploader so it can exit (or finish draining).
    uploaderWakerRef.current?.resolve();
  }, [
    clipLengthSec, intervalSec, selectedVehicleId, unmixMethod,
    tripStartKm, tripPurpose, micLocation,
    numCylinders, numStrokes, numValvesPerCylinder,
    syncQueueCount, uploaderLoop,
  ]);

  const stop = useCallback(async () => {
    cancelRef.current.cancelled = true;
    setRunning(false);
    setStatus('stopping…');
    setLiveAnalyser(null);
    await recorderRef.current?.dispose();
    recorderRef.current = null;
    recorderActiveRef.current = false;
    // Wake any waiting loops so they observe the cancel flag.
    uploaderWakerRef.current?.resolve();
    if (pendingAckRef.current) {
      const cb = pendingAckRef.current;
      pendingAckRef.current = null;
      cb.reject(new Error('cancelled'));
    }
    if (wsRef.current) {
      try { wsRef.current.close(); } catch { /* ignore */ }
      wsRef.current = null;
      setWsConnected(false);
    }
    setStatus('idle');
  }, []);

  useEffect(() => () => { void stop(); }, [stop]);

  async function logout() {
    try { await api.post('/api/logout'); } catch { /* ignore */ }
    onLogout();
  }

  async function handleVehicleAdded(v: Vehicle) {
    setVehicles((prev) => [...prev, v].sort((a, b) => a.name.localeCompare(b.name)));
    setSelectedVehicleId(v.id);
    setShowAddVehicle(false);
  }

  // Status banner for offline + queued state.
  const offline = !wsConnected && (running || queueCount > 0);

  return (
    <div className="app">
      {/* Header — Engine Acoustics + vehicle subtitle, username, sign out */}
      <div className="header">
        <div>
          <div className="brand">Engine Acoustics</div>
          <div className="vehicle-name">
            {selectedVehicle
              ? selectedVehicle.name
              : <span className="placeholder">— no vehicle selected —</span>}
          </div>
        </div>
        <div className="row" style={{ alignItems: 'center' }}>
          <span className="muted">{username}</span>
          <button className="secondary" onClick={logout}>Sign out</button>
        </div>
      </div>

      {/* Slim control bar: vehicle picker, add, configure, status, start/stop */}
      <div className="control-bar">
        <div className="row" style={{ alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <select
            value={selectedVehicleId ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setSelectedVehicleId(v === '' ? null : Number(v));
            }}
            disabled={running}
            style={{ minWidth: 180 }}
          >
            <option value="">— pick a vehicle —</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
          <button
            className="secondary"
            onClick={() => setShowAddVehicle(true)}
            disabled={running}
          >
            + Add vehicle
          </button>
          <button
            className="secondary"
            onClick={() => setShowConfig(true)}
          >
            ⚙ Configure
          </button>
          <button
            className="secondary"
            onClick={() => setShowCalInstructions(true)}
            title="Calibration & usage procedures"
          >
            ? Cal instructions
          </button>
          <span className="muted" style={{ flex: 1, fontSize: '0.85rem' }}>{status}</span>
          {queueCount > 0 && (
            <span
              className={offline ? 'tag warn' : 'tag'}
              title={offline ? 'offline — clips queued in browser storage' : 'clips queued for upload'}
            >
              {offline ? '⚠ offline · ' : ''}{queueCount} queued
            </span>
          )}
          {running ? (
            <button className="danger" onClick={stop}>Stop</button>
          ) : (
            <button
              onClick={start}
              disabled={
                selectedVehicleId == null ||
                tripStartKm.trim() === '' ||
                tripPurpose.trim() === ''
              }
            >Start</button>
          )}
        </div>

        {/* Vehicle-trip inputs (km + purpose). Hidden while running so
            the live engine monitor below has more vertical space — the
            inputs can't be edited mid-recording anyway. A compact
            summary line replaces them so the operator can confirm what
            was set without stopping the recording. */}
        {running ? (
          <div className="muted" style={{ marginTop: 6, fontSize: '0.78rem' }}>
            Trip: <strong style={{ color: 'var(--fg)' }}>{tripStartKm} km</strong>
            {' · '}
            <strong style={{ color: 'var(--fg)' }}>{tripPurpose || '(no purpose set)'}</strong>
          </div>
        ) : (
          <div className="trip-row">
            <div className="field" style={{ marginBottom: 0, flex: '0 0 160px' }}>
              <label>Trip Start Odometer Reading</label>
              <input
                type="number" min={0} max={9999999} step={1}
                value={tripStartKm}
                onChange={(e) => setTripStartKm(e.target.value)}
                placeholder="e.g. 123456"
                style={{ width: '100%' }}
              />
            </div>
            <div className="field" style={{ marginBottom: 0, flex: 1, minWidth: 220 }}>
              <label>Trip purpose</label>
              <input
                type="text"
                list="purpose-history"
                value={tripPurpose}
                onChange={(e) => setTripPurpose(e.target.value)}
                maxLength={200}
                placeholder="e.g. morning commute, dyno run, post-tune-up test"
                style={{ width: '100%' }}
              />
              <datalist id="purpose-history">
                {purposeHistory.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </div>
          </div>
        )}

        {running && activeMic && (
          <div
            style={{
              marginTop: 6, fontSize: '0.85rem',
              color: micWarning ? 'var(--danger)' : 'var(--muted)',
            }}
          >
            <strong>Mic:</strong> {activeMic.label || '(label unavailable)'}
            {activeMic.deviceId && (
              <span className="muted" style={{ marginLeft: 6, fontSize: '0.75rem' }}>
                · id {activeMic.deviceId.slice(0, 8)}…
              </span>
            )}
          </div>
        )}
        {micWarning && (
          <div className="error" style={{ marginTop: 4, fontSize: '0.85rem' }}>
            ⚠ {micWarning}
          </div>
        )}
        {error && <div className="error" style={{ marginTop: 6 }}>{error}</div>}
        {evictedTotal > 0 && (
          <div className="error" style={{ marginTop: 6, fontSize: '0.85rem' }}>
            Buffer full: {evictedTotal} oldest clip{evictedTotal === 1 ? '' : 's'} dropped
            (max {queue.QUEUE_CAP}). Reconnect to drain.
          </div>
        )}
      </div>

      {/* Live engine monitor — visible only while recording. Pulls
          from the recorder's tee'd AnalyserNode (level meter + live
          RPM detector + intensity trace). Confidence-builder for the
          user that the app is actually tracking what they're driving;
          the server-side analysis still does the heavy lifting per
          clip. */}
      {running && liveAnalyser && (
        <LiveEnginePanel
          analyser={liveAnalyser}
          config={{
            numCylinders,
            numStrokes: numStrokes === 2 ? 2 : 4,
            powertrainKind: selectedVehicle?.powertrain_kind ?? 'engine',
          }}
        />
      )}

      {/* Ground-truth feedback panel — uncertainty-gated questions
          surface here after recordings are analyzed. The user can
          answer or Ignore each one. Hidden entirely until there's
          something to show (no pending + nothing answered yet). */}
      <FeedbackPanel
        selectedVehicle={selectedVehicle}
        recordingActive={running}
        refreshSignal={feedbackRefreshTick}
      />

      {/* Live data panel — most prominent, top of the content area */}
      <div className="panel">
        <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>
            Live {liveMode === 'rms' ? 'RMS'
                : liveMode === 'spectrogram' ? 'spectrogram'
                : 'components'}
            {(() => {
              // Most-recent segment's RPM, shown in every live view
              // (RMS / spectrogram / components) — RPM detection now
              // runs for every method, not just ICA.
              const last = liveClips[liveClips.length - 1];
              if (!last) return null;
              if (last.rpm != null) {
                return (
                  <span style={{
                    marginLeft: '0.6rem', fontSize: '0.95rem', fontWeight: 500,
                    color: 'var(--accent)',
                  }}>· RPM {Math.round(last.rpm)}</span>
                );
              }
              if (last.rpmError) {
                return (
                  <span style={{
                    marginLeft: '0.6rem', fontSize: '0.85rem',
                    color: 'var(--danger)',
                  }}>· RPM n/a</span>
                );
              }
              return null;
            })()}
          </h3>
          <div className="row" style={{ alignItems: 'center' }}>
            {showLive && liveClips.length > 0 && (
              <span className="muted" style={{ fontSize: '0.85rem' }}>
                {liveClips.length}/{LIVE_MAX_SEGMENTS} segments
                {liveUpdatedAt && ` · updated ${new Date(liveUpdatedAt).toLocaleTimeString()}`}
              </span>
            )}
            <div className="row" style={{ gap: '0.25rem' }}>
              <button
                className="secondary"
                onClick={() => setLiveMode('rms')}
                style={liveMode === 'rms' ? { background: 'var(--accent-2)', color: 'white' } : undefined}
                aria-pressed={liveMode === 'rms'}
              >
                RMS
              </button>
              <button
                className="secondary"
                onClick={() => setLiveMode('spectrogram')}
                style={liveMode === 'spectrogram' ? { background: 'var(--accent-2)', color: 'white' } : undefined}
                aria-pressed={liveMode === 'spectrogram'}
              >
                Spectrogram
              </button>
              <button
                className="secondary"
                onClick={() => setLiveMode('components')}
                style={liveMode === 'components' ? { background: 'var(--accent-2)', color: 'white' } : undefined}
                aria-pressed={liveMode === 'components'}
              >
                Components
              </button>
            </div>
            <button
              className="secondary"
              onClick={() => setShowLive((v) => !v)}
              title={showLive ? 'click to hide' : 'click to show'}
            >
              Live plot: {showLive ? 'ON' : 'OFF'}
            </button>
            {showLive && liveClips.length > 0 && (
              <button className="secondary" onClick={() => setLiveClips([])}>Clear</button>
            )}
          </div>
        </div>
        {showLive && (
          <div style={{ marginTop: '0.5rem' }}>
            {liveMode === 'rms' ? (
              <LiveRmsChart
                rms={liveRms}
                clipBoundaries={liveClipBoundaries}
                segmentSec={clipLengthSec}
                numSegments={liveClips.length}
              />
            ) : liveClips.length === 0 ? (
              <div className="muted" style={{ padding: '1rem 0' }}>collecting samples…</div>
            ) : liveMode === 'spectrogram' ? (
              <>
                <LiveSpectrogramChart
                  segments={liveSpectrogramSegments}
                  height={180}
                  segmentSec={clipLengthSec}
                  sampleRate={clips[0]?.sampleRate}
                />
                <div className="muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>
                  Each strip is the spectrogram of one recorded segment in time;
                  light-gray gaps mark the (non-continuous) pauses between
                  recordings. Frequency: low at bottom, high at top.
                  Color: viridis, dark = quiet (≤ −90 dB), bright = loud (≥ 0 dB).
                </div>
              </>
            ) : (
              <>
                <div className="row" style={{ alignItems: 'center', marginBottom: 6 }}>
                  <span className="muted" style={{ fontSize: '0.85rem' }}>
                    Decomposition method (applied to next clip):
                  </span>
                  <div className="row" style={{ gap: '0.25rem' }}>
                    <button
                      className="secondary"
                      onClick={() => setUnmixMethod('block')}
                      style={unmixMethod === 'block'
                        ? { background: 'var(--accent-2)', color: 'white' }
                        : undefined}
                      aria-pressed={unmixMethod === 'block'}
                      title="Block reshape: fast (~0.4s), block-boundary artifacts"
                    >
                      Block reshape
                    </button>
                    <button
                      className="secondary"
                      onClick={() => setUnmixMethod('ssa')}
                      style={unmixMethod === 'ssa'
                        ? { background: 'var(--accent-2)', color: 'white' }
                        : undefined}
                      aria-pressed={unmixMethod === 'ssa'}
                      title="Singular Spectrum Analysis: slower (~1.5s), Hankel/Toeplitz, no block artifacts"
                    >
                      SSA
                    </button>
                    <button
                      className="secondary"
                      onClick={() => setUnmixMethod('ica')}
                      style={unmixMethod === 'ica'
                        ? { background: 'var(--accent-2)', color: 'white' }
                        : undefined}
                      aria-pressed={unmixMethod === 'ica'}
                      title="Cycle-aligned ICA: detects firing rate via autocorrelation, ICA on 10 stacked engine cycles. Returns 10 source traces."
                    >
                      ICA
                    </button>
                    <button
                      className="secondary"
                      onClick={() => setUnmixMethod('nmf')}
                      style={unmixMethod === 'nmf'
                        ? { background: 'var(--accent-2)', color: 'white' }
                        : undefined}
                      aria-pressed={unmixMethod === 'nmf'}
                      title="Cycle-aligned NMF: factors a per-cycle power-spectrogram into 10 non-negative spectral patterns × cycle-phase activations."
                    >
                      NMF
                    </button>
                  </div>
                </div>
                <IcaRpmBanner liveClips={liveClips} />
                <LiveComponentsChart
                  segments={liveComponentSegments}
                  height={260}
                  segmentSec={clipLengthSec}
                />
                <div className="muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>
                  Top-variance SVD components for each recorded segment, labeled
                  comp1…comp{liveComponentSegments[0]?.components.length ?? 0} (top down).
                  Light-gray gaps mark the non-continuous pauses between recordings.
                  Each row is independently scaled to its own min/max so smaller
                  components stay legible. Each segment shows the method (
                  {liveClips.map((c) => c.method).slice(-1).join('') || unmixMethod}
                  ) used at recording time — switching the method only affects future clips.
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Stats summary + capped recent-clips list */}
      <div className="panel">
        <div className="row" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ marginTop: 0, marginBottom: 0 }}>
            Recent clips <span className="muted" style={{ fontWeight: 400, fontSize: '0.85rem' }}>
              (most recent {MAX_RECENT_CLIPS})
            </span>
          </h3>
          {selectedVehicleId != null && (
            <button
              className="secondary"
              onClick={() => setShowTrends(true)}
              title="View history of stats over time across all recordings of this vehicle"
            >View history</button>
          )}
        </div>
        <div className="kpi-row">
          <span>Session total<strong>{sessionTotal}</strong></span>
          <span>Session errors<strong>{sessionErrors}</strong></span>
          <span>Visible<strong>{visibleStats.count}/{MAX_RECENT_CLIPS}</strong></span>
          {visibleStats.blocks > 0 && (
            <>
              <span>Blocks<strong>{visibleStats.blocks}</strong></span>
              <span>Mean RMS<strong>{visibleStats.mean.toFixed(4)}</strong></span>
              <span>Min RMS<strong>{visibleStats.min.toFixed(4)}</strong></span>
              <span>Peak RMS<strong>{visibleStats.max.toFixed(4)}</strong></span>
            </>
          )}
        </div>
        {clips.length === 0 && <div className="muted">no clips yet</div>}
        <div className="recording-list">
          {clips.map((c) => (
            <div className="recording-item" key={c.startedAt}>
              <div>
                {c.result?.components.rpm != null && (
                  <div style={{
                    fontSize: '0.85rem', fontWeight: 600,
                    color: 'var(--accent)', marginBottom: 2,
                  }}>
                    {Math.round(c.result.components.rpm)} RPM
                  </div>
                )}
                {c.result?.components.rpm == null
                  && (c.result?.components.rpm_error || c.result?.components.error) && (
                  <div className="error" style={{ fontSize: '0.75rem', marginBottom: 2 }}>
                    RPM n/a
                  </div>
                )}
                <div>{new Date(c.startedAt).toLocaleTimeString()}</div>
                <div className="recording-meta">
                  {c.durationSec}s @ {c.sampleRate}Hz
                  {c.sizeBytes != null && ` · ${(c.sizeBytes / 1024).toFixed(1)} KB`}
                </div>
              </div>
              <div>
                {c.result ? (
                  liveMode === 'rms' ? (
                    <RmsChart rms={c.result.rms.rms} durationSec={c.durationSec} />
                  ) : liveMode === 'spectrogram' ? (
                    <LiveSpectrogramChart
                      segments={[{
                        data: c.result.spectrogram.data,
                        numBlocks: c.result.spectrogram.num_blocks,
                        numBins: c.result.spectrogram.num_bins,
                      }]}
                      height={64}
                      segmentSec={c.durationSec}
                      sampleRate={c.sampleRate}
                    />
                  ) : (
                    <LiveComponentsChart
                      segments={[{ components: c.result.components.components }]}
                      height={Math.max(80, 18 * c.result.components.num_components)}
                      segmentSec={c.durationSec}
                    />
                  )
                ) : (
                  <div className="muted">{c.error ?? c.status}</div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.3rem' }}>
                <span className={
                  c.status === 'done' ? 'tag ok'
                  : c.status === 'error' ? 'tag error'
                  : 'tag warn'
                }>{c.status}</span>
                {c.recordingId != null && (
                  <div className="row" style={{ gap: '0.25rem' }}>
                    <button
                      className="secondary"
                      style={{ fontSize: '0.75rem', padding: '0.2rem 0.55rem' }}
                      onClick={() => setLabelTarget(c.startedAt)}
                    >Label</button>
                    {c.result?.components.cylinders
                      && c.result.components.cylinders.cylinders.length > 0 && (
                      <button
                        className="secondary"
                        style={{ fontSize: '0.75rem', padding: '0.2rem 0.55rem' }}
                        onClick={() => setDiagnoseTarget(c.startedAt)}
                        title="Per-cylinder diagnostic"
                      >Diagnose</button>
                    )}
                    {c.result?.components.cylinders
                      && c.result.components.cylinders.cylinders.length > 0
                      && selectedVehicleId != null && (() => {
                        const isBaseline =
                          selectedVehicle?.baseline_recording_id === c.recordingId;
                        return (
                          <button
                            className="secondary"
                            style={{
                              fontSize: '0.75rem', padding: '0.2rem 0.55rem',
                              borderColor: isBaseline ? 'var(--ok)' : undefined,
                              color: isBaseline ? 'var(--ok)' : undefined,
                            }}
                            onClick={() => void saveAsBaseline(c.recordingId!)}
                            title={isBaseline
                              ? 'This recording IS the current baseline. Click to clear.'
                              : 'Mark this recording as the vehicle\'s baseline for drift comparisons.'}
                          >{isBaseline ? '★ Baseline' : 'Set baseline'}</button>
                        );
                      })()}
                  </div>
                )}
                {c.labels && hasAnyLabel(c.labels) && (
                  <ClipLabelBadges labels={c.labels} />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modals */}
      {showConfig && (
        <Modal title="Configure recording" onClose={() => setShowConfig(false)}>
          <div className="field">
            <label>Audio input device</label>
            <select
              value={deviceId} onChange={(e) => setDeviceId(e.target.value)}
              style={{ width: '100%' }} disabled={running}
            >
              {devices.length === 0 && <option value="">(no devices)</option>}
              {devices.map((d) => (
                <option key={d.deviceId} value={d.deviceId}>
                  {d.label || `device ${d.deviceId.slice(0, 6)}`}
                </option>
              ))}
            </select>
            <div className="muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>
              <strong>Android Auto / CarPlay note:</strong> when the phone
              is connected to the car's infotainment system, the OS may
              re-route the mic input regardless of what's selected here.
              While recording, the control bar shows the actually-attached
              mic — if it doesn't match what you picked, disable Android
              Auto / CarPlay (or use airplane mode) and Refresh.
            </div>
            <button
              type="button"
              className="secondary"
              style={{ marginTop: 6, fontSize: '0.85rem', padding: '0.3rem 0.7rem' }}
              onClick={() => void refreshDevices()}
            >
              Refresh device list
            </button>
          </div>
          <div className="field">
            <label>Microphone location</label>
            <select
              value={micLocation}
              onChange={(e) => setMicLocation(e.target.value as MicLocation)}
              style={{ width: '100%' }}
              disabled={running}
            >
              <option value="phone_cab">{MIC_LOCATION_LABELS.phone_cab}</option>
              <option value="phone_hood">{MIC_LOCATION_LABELS.phone_hood}</option>
              <option value="external_hood">{MIC_LOCATION_LABELS.external_hood}</option>
            </select>
            <div className="muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>
              Stored in each clip's metadata so analyses can be compared across mic placements.
            </div>
          </div>
          <div className="field" style={{ marginBottom: 4 }}>
            <label>
              Powertrain
              {selectedVehicle && (
                <span className="muted" style={{ fontWeight: 400 }}>
                  {' '}— for <strong>{selectedVehicle.name}</strong>
                </span>
              )}
            </label>
            <select
              value={selectedVehicle?.powertrain_kind ?? 'engine'}
              onChange={(e) =>
                void updateVehicleEngine({ powertrain_kind: e.target.value as Vehicle['powertrain_kind'] })}
              disabled={running || !selectedVehicle}
              style={{ width: '100%' }}
            >
              <option value="engine">Engine only (ICE)</option>
              <option value="hybrid">Hybrid (ICE + electric)</option>
              <option value="ev">Electric only (EV)</option>
            </select>
            <div className="muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>
              {selectedVehicle?.powertrain_kind === 'hybrid'
                ? 'The live monitor will show whether the engine is running or the vehicle is in EV mode, with a state badge that flips as the powertrain switches.'
                : selectedVehicle?.powertrain_kind === 'ev'
                ? 'EV-only — no firing-rate analysis. The app still records audio for inverter / motor / road-noise diagnostics.'
                : 'ICE only — the firing-rate detectors below assume the engine is running for every recording.'}
            </div>
          </div>
          {selectedVehicle?.powertrain_kind !== 'ev' && (
          <>
          <div className="field" style={{ marginBottom: 4 }}>
            <label>
              Engine config
              {selectedVehicle && (
                <span className="muted" style={{ fontWeight: 400 }}>
                  {' '}— for <strong>{selectedVehicle.name}</strong>
                </span>
              )}
            </label>
            {!selectedVehicle && (
              <div className="muted" style={{ fontSize: '0.85rem' }}>
                Select a vehicle first — engine config is stored per-vehicle.
              </div>
            )}
          </div>
          <div className="row">
            <div className="field" style={{ flex: 1 }}>
              <label># cylinders</label>
              <select
                value={numCylinders}
                onChange={(e) =>
                  void updateVehicleEngine({ num_cylinders: Number(e.target.value) })}
                disabled={running || !selectedVehicle}
                style={{ width: '100%' }}
              >
                {ONE_TO_24.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>Engine type</label>
              <select
                value={numStrokes}
                onChange={(e) =>
                  void updateVehicleEngine({
                    num_strokes: Number(e.target.value) === 2 ? 2 : 4,
                  })}
                disabled={running || !selectedVehicle}
                style={{ width: '100%' }}
              >
                <option value={4}>4-stroke</option>
                <option value={2}>2-stroke</option>
              </select>
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>Valves / cylinder</label>
              <select
                value={numValvesPerCylinder}
                onChange={(e) =>
                  void updateVehicleEngine({ num_valves_per_cylinder: Number(e.target.value) })}
                disabled={running || !selectedVehicle}
                style={{ width: '100%' }}
              >
                {ONE_TO_24.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
          <div className="muted" style={{ fontSize: '0.8rem', marginTop: -6 }}>
            Total valves: {numCylinders} × {numValvesPerCylinder} ={' '}
            <strong>{numCylinders * numValvesPerCylinder}</strong>.
            Saved per-vehicle; switching the dropdown swaps these values.
            Cylinders + engine type drive the ICA cycle alignment; valve
            count is captured for downstream analyses.
          </div>
          <div className="field" style={{ marginTop: 6 }}>
            <label>Firing order (optional)</label>
            <input
              type="text"
              value={selectedVehicle?.firing_order ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                void updateVehicleEngine({
                  firing_order: raw.trim() === '' ? null : raw,
                });
              }}
              disabled={running || !selectedVehicle}
              placeholder={`e.g. ${Array.from({ length: numCylinders }, (_, i) => i + 1).join('-')}`}
              style={{ width: '100%' }}
            />
            <div className="muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>
              List of cylinders in firing sequence, separated by dashes.
              Used to label cylinders in the diagnostic dashboard. When
              unset, cylinders are labeled by slot index (1..N).
            </div>
          </div>
          </>
          )}
          <div className="row">
            <div className="field" style={{ flex: 1 }}>
              <label>Clip length (s)</label>
              <input
                type="number" min={1} max={120} step={1}
                value={clipLengthSec}
                onChange={(e) => setClipLengthSec(Math.max(1, Number(e.target.value) || 1))}
                disabled={running}
                style={{ width: '100%' }}
              />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>Interval (s)</label>
              <input
                type="number" min={1} max={3600} step={1}
                value={intervalSec}
                onChange={(e) => setIntervalSec(Math.max(1, Number(e.target.value) || 1))}
                disabled={running}
                style={{ width: '100%' }}
              />
            </div>
          </div>
          {running && (
            <div className="muted" style={{ fontSize: '0.85rem' }}>
              Stop recording to change these values.
            </div>
          )}
          {selectedVehicle && selectedVehicle.powertrain_kind !== 'ev' && (
            <div className="field" style={{ marginTop: 10 }}>
              <label>Cylinder calibration</label>
              <div className="muted" style={{ fontSize: '0.78rem', marginBottom: 4 }}>
                Map each persistent ID (P-A, P-B…) the app has identified
                to your engine's physical cylinder numbers. Only useful
                once the rotation alignment has been bootstrapped — see
                "Cyl alignment" in the diagnostic modal.
              </div>
              <button
                className="secondary"
                onClick={() => setShowCalibrate(true)}
                disabled={running}
              >
                Open calibration…
              </button>
            </div>
          )}
          <div className="row" style={{ marginTop: 8, justifyContent: 'flex-end' }}>
            <button className="secondary" onClick={() => setShowConfig(false)}>Done</button>
          </div>
        </Modal>
      )}
      {showCalibrate && selectedVehicle && (
        <CalibrateCylindersModal
          vehicle={selectedVehicle}
          onClose={() => setShowCalibrate(false)}
          onSaved={(next) => {
            setVehicles((prev) =>
              prev.map((v) => (v.id === next.id ? next : v)),
            );
          }}
        />
      )}
      {showCalInstructions && (
        <CalInstructionsModal onClose={() => setShowCalInstructions(false)} />
      )}
      {showAddVehicle && (
        <AddVehicleModal
          onClose={() => setShowAddVehicle(false)}
          onAdded={handleVehicleAdded}
        />
      )}
      {showTrends && selectedVehicleId != null && selectedVehicle && (
        <TrendsModal
          vehicleId={selectedVehicleId}
          vehicleName={selectedVehicle.name}
          onClose={() => setShowTrends(false)}
        />
      )}
      {diagnoseTarget != null && (() => {
        const target = clips.find((c) => c.startedAt === diagnoseTarget);
        if (!target?.result) {
          setDiagnoseTarget(null);
          return null;
        }
        // Don't pass a baseline if we're already looking AT the
        // baseline recording — drift against self is always zero.
        const isViewingBaseline =
          selectedVehicle?.baseline_recording_id === target.recordingId;
        return (
          <DiagnosticModal
            result={target.result}
            recordingId={target.recordingId}
            vehicleId={selectedVehicleId ?? undefined}
            vehicleName={selectedVehicle?.name}
            baseline={isViewingBaseline ? null : baselineAnalysis}
            cylAssignmentsJson={selectedVehicle?.persistent_cyl_assignments ?? null}
            onClose={() => setDiagnoseTarget(null)}
          />
        );
      })()}
      {labelTarget != null && (() => {
        const target = clips.find((c) => c.startedAt === labelTarget);
        if (!target || target.recordingId == null) {
          // Target vanished (eviction). Close silently.
          setLabelTarget(null);
          return null;
        }
        return (
          <LabelModal
            recordingId={target.recordingId}
            initial={target.labels ?? emptyLabels()}
            onClose={() => setLabelTarget(null)}
            onSaved={(saved) => {
              setClips((prev) => prev.map((c) =>
                c.startedAt === labelTarget ? { ...c, labels: saved } : c,
              ));
              setLabelTarget(null);
            }}
          />
        );
      })()}
    </div>
  );
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// ---- RPM banner shown above the live components chart for ICA -----------
//
// Reads the most recent ICA segment from `liveClips`. If detection
// succeeded, shows the estimated RPM. If it failed, shows the diagnostic
// the Octave layer surfaced (e.g. "no clear firing-rate period in
// [33.33, 266.67] Hz, max ACF = 0.04"). Renders nothing when the most
// recent segment came from a non-ICA method.

interface IcaRpmBannerProps { liveClips: LiveSegmentData[]; }

// Banner above the live components chart. Surfaces the consensus across
// four RPM detectors (YIN-ACF, cepstrum, harmonic-sum, STFT) plus an
// agreement indicator. When the four disagree, the disagreement IS the
// diagnostic — typically misfire / bank-asymmetry locking onto a
// sub-harmonic. Click to expand and see each detector's value.
function IcaRpmBanner({ liveClips }: IcaRpmBannerProps) {
  const [expanded, setExpanded] = useState(false);
  const last = liveClips[liveClips.length - 1];
  if (!last) return null;
  if (last.rpm != null) {
    const ok = (last.rpmAgreement ?? 'none') === 'all';
    const okMost = (last.rpmAgreement ?? 'none') === 'majority';
    const numAgree = countAgreeMethods(last.rpmMethods, last.rpm);
    const total = last.rpmMethods?.length ?? 0;
    const agreeColor = ok ? 'var(--ok)'
                     : okMost ? '#f59e0b'
                     : 'var(--danger)';
    const wasCorrected = last.rpmCorrection && last.rpmCorrection !== 'none'
      && last.rpmRaw != null && Math.abs((last.rpmRaw - last.rpm)) > 0.01 * last.rpm;
    return (
      <div
        style={{
          marginBottom: 8, fontSize: '0.9rem',
          background: 'var(--panel-2)', border: '1px solid var(--border)',
          borderRadius: 6, padding: '0.4rem 0.7rem',
        }}
      >
        <div className="row" style={{ alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
          <span className="muted">Estimated RPM:</span>
          <strong style={{ color: 'var(--fg)' }}>{Math.round(last.rpm)}</strong>
          {wasCorrected && (
            <span className="muted" style={{ fontSize: '0.75rem' }}>
              (raw {Math.round(last.rpmRaw!)}, harmonic-corrected via {last.rpmCorrection})
            </span>
          )}
          {total > 0 && (
            <span style={{ fontSize: '0.78rem', color: agreeColor }}>
              ({numAgree}/{total} agree, {last.rpmAgreement})
            </span>
          )}
          {total > 0 && (
            <button
              type="button"
              className="secondary"
              style={{ fontSize: '0.75rem', padding: '0.15rem 0.5rem', marginLeft: 'auto' }}
              onClick={() => setExpanded((v) => !v)}
            >{expanded ? 'hide' : 'detectors'}</button>
          )}
        </div>
        {last.rpmDiagnostic && last.rpmDiagnostic !== 'no harmonic locking — clean signal, no asymmetry detected' && (
          <div style={{
            marginTop: 4, fontSize: '0.78rem',
            color: wasCorrected ? '#f59e0b' : 'var(--muted)',
          }}>
            <strong>Diagnostic:</strong> {last.rpmDiagnostic}
          </div>
        )}
        {expanded && last.rpmMethods && (
          <RpmMethodTable methods={last.rpmMethods} consensus={last.rpm} />
        )}
      </div>
    );
  }
  if (last.rpmError) {
    return (
      <div className="error" style={{ marginBottom: 8, fontSize: '0.85rem' }}>
        RPM detection failed: {last.rpmError}
        {last.rpmMethods && (
          <RpmMethodTable methods={last.rpmMethods} consensus={null} />
        )}
      </div>
    );
  }
  return null;
}

function countAgreeMethods(
  methods: import('./lib/ws').RpmMethodResult[] | undefined,
  consensus: number,
): number {
  if (!methods) return 0;
  const TOL = 0.05;
  let n = 0;
  for (const m of methods) {
    if (m.rpm == null) continue;
    if (Math.abs(m.rpm - consensus) <= TOL * consensus) n++;
  }
  return n;
}

function RpmMethodTable({
  methods, consensus,
}: { methods: import('./lib/ws').RpmMethodResult[]; consensus: number | null }) {
  return (
    <div style={{ marginTop: 6, fontSize: '0.78rem', fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
      <div style={{ display: 'flex', gap: '0.7rem', color: 'var(--muted)', borderBottom: '1px solid var(--border)', paddingBottom: 2, marginBottom: 2 }}>
        <span style={{ width: 70 }}>method</span>
        <span style={{ width: 60, textAlign: 'right' }}>raw RPM</span>
        <span style={{ width: 30, textAlign: 'right' }}>×</span>
        <span style={{ width: 70, textAlign: 'right' }}>corrected</span>
      </div>
      {methods.map((m) => {
        const cv = m.corrected_rpm;
        const inCluster = consensus != null && cv != null
          && Math.abs(cv - consensus) <= 0.05 * consensus;
        const tone = m.error ? 'var(--muted)'
                   : inCluster ? 'var(--ok)'
                   : 'var(--danger)';
        // Lock-factor colour conveys the engine geometry / fault hint.
        const lfColor = m.lock_factor === 1 ? 'var(--ok)'
                      : m.lock_factor === 2 ? '#f59e0b'    // bank-alt
                      : m.lock_factor === 3 ? '#fb7185'    // per-rev
                      : m.lock_factor === 6 ? 'var(--danger)' // engine-cycle / misfire
                      : 'var(--muted)';
        return (
          <div key={m.method} style={{ display: 'flex', gap: '0.7rem', color: tone }}>
            <span style={{ width: 70 }}>{m.method}</span>
            <span style={{ width: 60, textAlign: 'right' }}>
              {m.rpm == null ? '—' : Math.round(m.rpm)}
            </span>
            <span style={{ width: 30, textAlign: 'right', color: lfColor }}>
              {m.lock_factor == null ? '—' : `×${m.lock_factor}`}
            </span>
            <span style={{ width: 70, textAlign: 'right' }}>
              {cv == null ? '—' : Math.round(cv)}
            </span>
            {m.error && (
              <span style={{ flex: 1, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {m.error}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---- Modal helpers ----

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
}

function Modal({ title, onClose, children }: ModalProps) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose} aria-label="close">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

interface AddVehicleProps {
  onClose: () => void;
  onAdded: (v: Vehicle) => void;
}

// ---- Clip labelling -----------------------------------------------------

function emptyLabels(): ClipLabels {
  return { gauge_rpm: null, powertrain: null, incident_keywords: null };
}

function hasAnyLabel(l: ClipLabels): boolean {
  return l.gauge_rpm != null || l.powertrain != null
    || (l.incident_keywords != null && l.incident_keywords.length > 0);
}

function ClipLabelBadges({ labels }: { labels: ClipLabels }) {
  const tags = labels.incident_keywords
    ? labels.incident_keywords.split(',').map((s) => s.trim()).filter(Boolean)
    : [];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end', maxWidth: 200 }}>
      {labels.gauge_rpm != null && (
        <span className="tag" title="RPM from dashboard gauge">
          gauge {labels.gauge_rpm}
        </span>
      )}
      {labels.powertrain != null && (
        <span className="tag" title="Powertrain type">{labels.powertrain}</span>
      )}
      {tags.map((t) => (
        <span key={t} className="tag" style={{ borderColor: '#f59e0b', color: '#f59e0b' }}>
          {t}
        </span>
      ))}
    </div>
  );
}

interface LabelModalProps {
  recordingId: number;
  initial: ClipLabels;
  onClose: () => void;
  onSaved: (labels: ClipLabels) => void;
}

function LabelModal({ recordingId, initial, onClose, onSaved }: LabelModalProps) {
  const [gauge, setGauge] = useState<string>(
    initial.gauge_rpm == null ? '' : String(initial.gauge_rpm),
  );
  const [pt, setPt] = useState<'' | 'engine' | 'ev'>(initial.powertrain ?? '');
  const [kw, setKw] = useState<string>(initial.incident_keywords ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    const body = {
      gauge_rpm: gauge.trim() === '' ? null : Number(gauge),
      powertrain: pt === '' ? null : pt,
      incident_keywords: kw.trim() === '' ? null : kw.trim(),
    };
    try {
      const saved = await api.put<ClipLabels>(
        `/api/recordings/${recordingId}/labels`, body,
      );
      onSaved({
        gauge_rpm: saved.gauge_rpm ?? null,
        powertrain: saved.powertrain ?? null,
        incident_keywords: saved.incident_keywords ?? null,
      });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={`Label recording #${recordingId}`} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="field">
          <label>RPM from gauge</label>
          <input
            type="number" min={0} max={20000} step={1}
            value={gauge}
            onChange={(e) => setGauge(e.target.value)}
            placeholder="e.g. 1500"
            style={{ width: '100%' }}
          />
          <div className="muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>
            What the dashboard gauge read at the moment of recording.
            Compared against the ICA-estimated RPM in the admin view.
          </div>
        </div>
        <div className="field">
          <label>Powertrain</label>
          <select
            value={pt}
            onChange={(e) => setPt(e.target.value as '' | 'engine' | 'ev')}
            style={{ width: '100%' }}
          >
            <option value="">— unspecified —</option>
            <option value="engine">Engine (combustion)</option>
            <option value="ev">EV (electric)</option>
          </select>
        </div>
        <div className="field">
          <label>Incident keywords</label>
          <input
            type="text"
            value={kw}
            onChange={(e) => setKw(e.target.value)}
            placeholder="e.g. knock, misfire, cold start"
            maxLength={500}
            style={{ width: '100%' }}
          />
          <div className="muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>
            Comma-separated. Up to 500 characters.
          </div>
        </div>
        {err && <div className="field error">{err}</div>}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="secondary" onClick={onClose}>Cancel</button>
          <button type="submit" disabled={busy}>{busy ? '…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
}

const ZERO_TO_8 = Array.from({ length: 9 }, (_, i) => i);

function AddVehicleModal({ onClose, onAdded }: AddVehicleProps) {
  const [name, setName] = useState('');
  const [vehicleType, setVehicleType] = useState<VehicleType>('automobile');
  // Automobile-only.
  const [powertrain, setPowertrain] = useState<Vehicle['powertrain_kind']>('engine');
  // Sailboat-only.
  const [sailPowertrain, setSailPowertrain] = useState<SailPowertrain>('diesel');
  const [numAlternators, setNumAlternators] = useState(1);
  // Shared engine config (automobile ICE/hybrid, or sailboat diesel).
  const [numCylinders, setNumCylinders] = useState(4);
  const [numStrokes, setNumStrokes] = useState<2 | 4>(4);
  const [numValvesPerCylinder, setNumValvesPerCylinder] = useState(4);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Whether the engine-config block applies to the current selection.
  const isSailboat = vehicleType === 'sailboat';
  const showEngineConfig = isSailboat
    ? sailPowertrain === 'diesel'
    : powertrain !== 'ev';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const body: Record<string, unknown> = { name, vehicle_type: vehicleType };
      if (isSailboat) {
        body.sail_powertrain = sailPowertrain;
        if (sailPowertrain === 'diesel') {
          body.num_alternators = numAlternators;
          body.num_cylinders = numCylinders;
          body.num_strokes = numStrokes;
          body.num_valves_per_cylinder = numValvesPerCylinder;
        }
      } else {
        body.powertrain_kind = powertrain;
        if (powertrain !== 'ev') {
          body.num_cylinders = numCylinders;
          body.num_strokes = numStrokes;
          body.num_valves_per_cylinder = numValvesPerCylinder;
        }
      }
      const v = await api.post<Vehicle>('/api/vehicles', body);
      onAdded(v);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const engineConfigFields = (
    <>
      <div className="field" style={{ marginBottom: 4 }}>
        <label>{isSailboat ? 'Diesel engine config' : 'Engine config'}</label>
      </div>
      <div className="row">
        <div className="field" style={{ flex: 1 }}>
          <label># cylinders</label>
          <select
            value={numCylinders}
            onChange={(e) => setNumCylinders(Number(e.target.value))}
            style={{ width: '100%' }}
          >
            {ONE_TO_24.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>Engine type</label>
          <select
            value={numStrokes}
            onChange={(e) => setNumStrokes(Number(e.target.value) === 2 ? 2 : 4)}
            style={{ width: '100%' }}
          >
            <option value={4}>4-stroke</option>
            <option value={2}>2-stroke</option>
          </select>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>Valves / cyl</label>
          <select
            value={numValvesPerCylinder}
            onChange={(e) => setNumValvesPerCylinder(Number(e.target.value))}
            style={{ width: '100%' }}
          >
            {ONE_TO_24.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>
      <div className="muted" style={{ fontSize: '0.8rem', marginTop: -2, marginBottom: 8 }}>
        Editable later from the Configuration modal once the vehicle is selected.
      </div>
    </>
  );

  return (
    <Modal title="Add vehicle" onClose={onClose}>
      <form onSubmit={submit}>
        <div className="field">
          <label>Vehicle name</label>
          <input
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Honda Civic 2018, S/V Windrose"
            style={{ width: '100%' }}
            autoFocus required maxLength={64}
          />
          <div className="muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>
            Used to keep recordings separated per vehicle. Up to 64 characters.
          </div>
        </div>
        <div className="field">
          <label>Vehicle type</label>
          <select
            value={vehicleType}
            onChange={(e) => setVehicleType(e.target.value as VehicleType)}
            style={{ width: '100%' }}
          >
            <option value="automobile">Automobile</option>
            <option value="sailboat">Sailboat</option>
          </select>
        </div>

        {isSailboat ? (
          <>
            <div className="field">
              <label>Powertrain</label>
              <select
                value={sailPowertrain}
                onChange={(e) => setSailPowertrain(e.target.value as SailPowertrain)}
                style={{ width: '100%' }}
              >
                <option value="diesel">Diesel engine + sails</option>
                <option value="sails_only">Sails only (no engine)</option>
              </select>
              <div className="muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>
                {sailPowertrain === 'diesel'
                  ? 'The firing-rate detectors monitor the diesel auxiliary while it runs (charging / motoring).'
                  : 'No engine — no firing-rate analysis. The app still records audio for rigging / hull / water-noise diagnostics.'}
              </div>
            </div>
            {sailPowertrain === 'diesel' && (
              <div className="field">
                <label># alternators</label>
                <select
                  value={numAlternators}
                  onChange={(e) => setNumAlternators(Number(e.target.value))}
                  style={{ width: '100%' }}
                >
                  {ZERO_TO_8.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <div className="muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>
                  Battery-charging alternators driven off the diesel (0–8).
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="field">
            <label>Powertrain</label>
            <select
              value={powertrain}
              onChange={(e) => setPowertrain(e.target.value as Vehicle['powertrain_kind'])}
              style={{ width: '100%' }}
            >
              <option value="engine">Engine only (ICE)</option>
              <option value="hybrid">Hybrid (ICE + electric)</option>
              <option value="ev">Electric only (EV)</option>
            </select>
          </div>
        )}

        {showEngineConfig && engineConfigFields}
        {err && <div className="field error">{err}</div>}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="secondary" onClick={onClose}>Cancel</button>
          <button type="submit" disabled={busy || !name.trim()}>{busy ? '…' : 'Add'}</button>
        </div>
      </form>
    </Modal>
  );
}
